import { useContext, useEffect, useState } from 'react'
import {
    Accordion, Button, Checkbox, RadioCard, Flex, Box, Text, TermsAcceptanceModal, NinEnforcementModal, ExemptVerificationModal,
} from 'src/componentv2'
import ConfigurationApiContext from 'src/provider/API/v2/call-service/configuration'
import {
    ACCEPTED_ID_TYPES_HEADING,
    ACCEPTED_ID_TYPES_HELP_TEXT,
    ACCEPTED_ID_TYPE_OPTIONS,
    EXEMPT_EXISTING_ID_VERIFICATION_HEADING,
    EXEMPT_EXISTING_ID_VERIFICATION_LABEL,
    ONBOARDING_TERMS_URL,
    NIN_ID_KEY,
} from 'src/utils/onboardingPolicyCopy'
import { OnboardingPolicyDetails } from 'src/model/v2/interface/params'

interface Props {
    open: boolean
    onToggle: (open: boolean) => void
    onSaveAndNext?: () => void
}

export const OnboardingFlowSettings: React.FC<Props> = ({ open, onToggle, onSaveAndNext }) => {
    const { setOnboardingPolicy, getOnboardingPolicy } = useContext(ConfigurationApiContext)

    const [mode, setMode] = useState<'streamlined' | 'default' | ''>('')
    // Security-sensitive: when on, returning customers skip identity/face verification during onboarding.
    // Default off. The backend enforces this flag; this toggle only sets it.
    const [exemptExistingCustomers, setExemptExistingCustomers] = useState(false)
    const [acceptedIds, setAcceptedIds] = useState<string[]>(['BVN'])
    const [agreed, setAgreed] = useState(false)
    const [termsOpen, setTermsOpen] = useState(false)
    const [ninModalOpen, setNinModalOpen] = useState(false)
    const [ninConfirmed, setNinConfirmed] = useState(false)
    const [exemptModalOpen, setExemptModalOpen] = useState(false)
    const [saving, setSaving] = useState(false)
    const [loadingPolicy, setLoadingPolicy] = useState(true)

    const toggleAcceptedId = (key: string) => {
        if (key === NIN_ID_KEY && !acceptedIds.includes(NIN_ID_KEY)) {
            if (ninConfirmed) {
                setAcceptedIds((prev) => [...prev, key])
            } else {
                setNinModalOpen(true)
            }
            return
        }
        setAcceptedIds((prev) =>
            prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key]
        )
    }

    const handleConfirmNin = () => {
        setNinConfirmed(true)
        setAcceptedIds((prev) => (prev.includes(NIN_ID_KEY) ? prev : [...prev, NIN_ID_KEY]))
        setNinModalOpen(false)
    }

    // Enabling the exemption is security-sensitive, so defer turning it on until the admin
    // acknowledges the risk in the modal. Disabling it (and the prefill) is immediate.
    const handleToggleExempt = (checked: boolean) => {
        if (checked && !exemptExistingCustomers) {
            setExemptModalOpen(true)
            return
        }
        setExemptExistingCustomers(checked)
    }

    const handleConfirmExempt = () => {
        setExemptExistingCustomers(true)
        setExemptModalOpen(false)
    }

    // Streamlined onboarding is the only mode that needs sign-off on the Terms and
    // Conditions, so selecting it prompts for agreement instead of switching immediately.
    // Once already agreed, re-selecting it is instant.
    const selectStreamlined = () => {
        if (agreed) {
            setMode('streamlined')
        } else {
            setTermsOpen(true)
        }
    }

    const handleAcceptTerms = () => {
        setAgreed(true)
        setMode('streamlined')
        setTermsOpen(false)
    }

    // Prefill `mode` from GET /Onboarding/Policy. There's no server-side field
    // for "terms accepted", so `agreed` always comes back unchecked on load —
    // that's expected, not a bug (re-agreeing each visit is the intended UX
    // until/unless a dedicated field is added).
    useEffect(() => {
        let cancelled = false

        ;(async () => {
            const data = await getOnboardingPolicy()
            if (!cancelled && data) {
                setMode(data.allowArbitraryPhoneNumber ? 'streamlined' : 'default')
                setExemptExistingCustomers(!!data.exemptIdVerificationForExistingCustomers)
                if (data.acceptedIds?.length) {
                    setAcceptedIds(data.acceptedIds)
                    if (data.acceptedIds.includes(NIN_ID_KEY)) setNinConfirmed(true)
                }
            }
            if (!cancelled) setLoadingPolicy(false)
        })()

        return () => {
            cancelled = true
        }
    }, [])

    const handleSave = async () => {
        if (!mode || (mode === 'streamlined' && !agreed) || acceptedIds.length === 0) return
        setSaving(true)

        // Typed against OnboardingPolicyDetails so a mistyped/missing field is a compile error
        // (setOnboardingPolicy itself accepts a loose config shape).
        const payload: OnboardingPolicyDetails = {
            allowArbitraryPhoneNumber: mode === 'streamlined',
            enforceFaceMatchAtOnboarding: true, // always true for now
            allowIdVerificationWithMetadata: false, // always false for now
            exemptIdVerificationForExistingCustomers: exemptExistingCustomers,
            acceptedIds,
        }
        const result = await setOnboardingPolicy(payload)

        setSaving(false)
        if (result !== false && result !== null) {
            onSaveAndNext?.()
        }
    }

    return (
        <Accordion
            title="Onboarding Flow settings"
            open={open}
            onToggle={onToggle}
        >
            <div className="form-row">
                <Box>
                    <Flex items="center" gap="0.8" bottomMargin="0.8">
                        <Text fontSize="1.4" style={{ fontFamily: 'Gilmer Medium' }}>
                            Streamlined Onboarding
                        </Text>
                        <span className="business-logic__badge">Recommended</span>
                    </Flex>
                    <RadioCard
                        name="onboarding-mode"
                        label="Allow users to enter their preferred phone number for verification"
                        helpText="Users can input any phone number and verify via OTP"
                        checked={mode === 'streamlined'}
                        disabled={loadingPolicy}
                        onChange={selectStreamlined}
                    />
                    <Box topMargin="1.2">
                        <Checkbox
                            id="agree-terms"
                            checked={agreed}
                            disabled={loadingPolicy}
                            onChange={setAgreed}
                            label={(
                                <>
                                    I have read and agreed to the{' '}
                                    <a onClick={() => setTermsOpen(true)}>Terms and Conditions</a>{' '}
                                    for streamlined onboarding.
                                </>
                            )}
                        />
                    </Box>
                </Box>

                <Box>
                    <Text fontSize="1.4" style={{ fontFamily: 'Gilmer Medium' }} bottomMargin="0.8">
                        Default Onboarding
                    </Text>
                    <RadioCard
                        name="onboarding-mode"
                        label="Use phone number attached to BVN or NIN for verification"
                        helpText="Phone number is pulled directly from BVN/NIN records"
                        checked={mode === 'default'}
                        disabled={loadingPolicy}
                        onChange={() => setMode('default')}
                    />
                </Box>
            </div>

            <Box topMargin="2.4">
                <Text fontSize="1.4" style={{ fontFamily: 'Gilmer Medium' }} bottomMargin="0.8">
                    {EXEMPT_EXISTING_ID_VERIFICATION_HEADING}
                </Text>
                <Checkbox
                    id="exempt-existing-id-verification"
                    checked={exemptExistingCustomers}
                    disabled={loadingPolicy}
                    onChange={handleToggleExempt}
                    label={EXEMPT_EXISTING_ID_VERIFICATION_LABEL}
                />
            </Box>

            <Box topMargin="2.4">
                <Text fontSize="1.4" style={{ fontFamily: 'Gilmer Medium' }} bottomMargin="0.8">
                    {ACCEPTED_ID_TYPES_HEADING}
                </Text>
                <Text fontSize="1.2" color="#667085" bottomMargin="0.8">
                    {ACCEPTED_ID_TYPES_HELP_TEXT}
                </Text>
                {ACCEPTED_ID_TYPE_OPTIONS.map((option) => (
                    <Checkbox
                        key={option.key}
                        id={`accepted-id-${option.key}`}
                        checked={acceptedIds.includes(option.key)}
                        disabled={loadingPolicy}
                        onChange={() => toggleAcceptedId(option.key)}
                        label={option.label}
                    />
                ))}
                {acceptedIds.length === 0 && (
                    <Text fontSize="1.2" color="#D92D20" topMargin="0.8">
                        Select at least one ID type.
                    </Text>
                )}
            </Box>

            <Flex justify="flex-end" topMargin="2.4" className="accordion__footer">
                <Button
                    variant="ghost"
                    size="sm"
                    disabled={!mode || (mode === 'streamlined' && !agreed) || acceptedIds.length === 0 || saving || loadingPolicy}
                    onClick={handleSave}
                >
                    {saving ? 'Saving…' : 'Save Changes'}
                </Button>
            </Flex>

            <TermsAcceptanceModal
                open={termsOpen}
                onClose={() => setTermsOpen(false)}
                onAccept={handleAcceptTerms}
                termsUrl={ONBOARDING_TERMS_URL}
            />

            <NinEnforcementModal
                open={ninModalOpen}
                onClose={() => setNinModalOpen(false)}
                onConfirm={handleConfirmNin}
            />

            <ExemptVerificationModal
                open={exemptModalOpen}
                onClose={() => setExemptModalOpen(false)}
                onConfirm={handleConfirmExempt}
            />
        </Accordion>
    )
}

export default OnboardingFlowSettings